
































import mixins from "vue-typed-mixins"
import { each, find, first, map } from 'lodash-es'

import { LayoutGroupExt, PropExtInfo } from "models"
import { DisplayLookup, LayoutLookup } from "models/layout"
import { NestedProp, PropChoice } from "models/schema"
import { FullSchema } from 'models/schema/stay'

import MRC from './MRC.vue'
import DisplayMixin from "../../mixins/Display.vue"
import InputMixin from "../../mixins/Input.vue"
import Generic from "../../sections/Generic.vue"

const displayInfo: DisplayLookup = {
    all: [
        {
            field: "strength_right_normal",
            force_label: "Right side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "strength_left_normal",
            force_label: "Left side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "facial_strength_right",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "facial_strength_left",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "arm_strength_right",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "arm_strength_left",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "leg_strength_right",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "leg_strength_left",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "ataxia_right_normal",
            force_label: "Right side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "ataxia_left_normal",
            force_label: "Left side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "ataxia_arm_right",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        },
        {
            field: "ataxia_arm_left",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        },
        {
            field: "ataxia_leg_right",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        },
        {
            field: "ataxia_leg_left",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        }
    ]
}

const layouts: LayoutLookup = {
    strength: [
        { fields: [ "strength_right_normal", "strength_left_normal" ], label: " ", label_col: 2, alignment: "inline" },
        { fields: [ "facial_strength_right", "facial_strength_left" ], label: "Facial strength", label_col: 2, alignment: "inline" },
        { fields: [ "arm_strength_right", "arm_strength_left" ], label: "Arm strength", label_col: 2, field_col: 5, alignment: "inline" },
        { fields: [ "leg_strength_right", "leg_strength_left" ], label: "Leg strength", label_col: 2, field_col: 5, alignment: "inline" }
    ],
    ataxia: [
        { fields: [ "ataxia_right_normal", "ataxia_left_normal" ], label: " ", label_col: 2, alignment: "inline" },
        { fields: [ "ataxia_arm_right", "ataxia_arm_left" ], label: "Arm coordination", label_col: 2, field_col: 5, alignment: "inline" },
        { fields: [ "ataxia_leg_right", "ataxia_leg_left" ], label: "Leg coordination", label_col: 2, field_col: 5, alignment: "inline" },
    ]
}

type Link = [string, string[]]

const thisComponent = mixins(DisplayMixin, InputMixin).extend({
    props: {
        parentHtmlId: {
            type: String,
            required: true
        },
        stay_id: {
            type: Number,
            required: true
        },
        section: {
            type: String as () => keyof FullSchema,
            required: true
        }
    },
    methods: {
        generateWatchers() {
            each(this.links, link => {
                const normal = link[0]
                const targets = link[1]

                this.$watch(normal, function(val: any, oldVal: any) {
                    if (val) {
                        each(targets, target => {
                            const res = this.getPropAndFirstChoice(this.allFields, target)
                            const propInfo = res[0],
                                choice = res[1]
                            this.setFieldVal(this.stay_id, propInfo.path, choice ? choice.value : false)
                        })
                    }
                })

                each(targets, target => {
                    this.$watch(target, function(val: any, oldVal: any) {
                        const res = this.getPropAndFirstChoice(this.allFields, target)
                        const propInfo = res[0],
                              choice = res[1]
                        const normalPropInfo = find(this.allFields, { name: normal })
                        if (!((choice && choice.value === val) || !val))
                            this.setFieldVal(this.stay_id, normalPropInfo!.path, false)
                    })
                })
            })
        }
    },
    computed: {
        schema(): NestedProp | undefined {
            return this.getSectionSchema(this.section)
        },
        statusMsg(): string {
            return this.schema ? "" : "Schema loading..."
        },
        allFields(): PropExtInfo[] {
            return this.schema
                ? this.generatePropExtInfo(this.section, this.schema.children, displayInfo.all)
                : []
        },
        strengthGroups(): LayoutGroupExt[] | undefined {
            return this.generateGroups(this.section, this.allFields, layouts.strength)
        },
        ataxiaGroups(): LayoutGroupExt[] | undefined {
            return this.generateGroups(this.section, this.allFields, layouts.ataxia)
        },
        /** NOTE: this gets replaced in beforeCreate, but we declare it here to accommodate Typescript prop check */
        links(): Link[] {
            return []
        }
    },
    beforeCreate() {
        const sides = ['right', 'left']
        const strengths = ['facial_strength', 'arm_strength', 'leg_strength']
        const ataxias = ['ataxia_arm', 'ataxia_leg']

        const props: string[] = []
        const links: Link[] = []

        each(sides, side => {
            const strengthNormal = `strength_${side}_normal`
            const strengthTargets = map(strengths, base => { return `${base}_${side}` })
            links.push([strengthNormal, strengthTargets])

            const ataxiaNormal = `ataxia_${side}_normal`
            const ataxiaTargets = map(ataxias, base => { return `${base}_${side}`})
            links.push([ataxiaNormal, ataxiaTargets])

            props.push(strengthNormal, ...strengthTargets, ataxiaNormal, ...ataxiaTargets)
        })

        const self = this
        each(props, prop => {
            const computed: any = {
                get() {
                    return this.getFieldVal(this.stay_id, `${this.section}.${prop}`)
                }
            }
            self.$options.computed![prop] = computed
        })

        this.$options.computed!['links'] = {
            get() { return links }
        }
    },
    created() {
        this.generateWatchers()
    },
    components: {
        Generic,
        mrc: MRC
    }
})

export default thisComponent
